import { CENTER_X, CENTER_Y, TAU } from './constants';

/**
 * Screen-polar convention:
 *   theta = 0     → east  (+x)
 *   theta = +π/2  → south (+y, screen down)
 *   theta = π     → west
 *   theta = -π/2  → north
 * Angles increase clockwise on screen because y points down.
 *
 * Everything in the sim is (theta, r). Cartesian conversion happens at draw
 * time only — that's what keeps collision to two float comparisons (§4.1).
 */

export const NORTH = -Math.PI / 2;
export const SOUTH = Math.PI / 2;
export const EAST = 0;
export const WEST = Math.PI;

/** Normalise to [0, TAU). */
export function norm(theta: number): number {
  const t = theta % TAU;
  return t < 0 ? t + TAU : t;
}

/** Normalise to (-π, π]. */
export function normSigned(theta: number): number {
  let t = (theta + Math.PI) % TAU;
  if (t < 0) t += TAU;
  return t - Math.PI;
}

/** Shortest signed angular distance from `a` to `b`, in (-π, π]. */
export function angleDelta(a: number, b: number): number {
  return normSigned(b - a);
}

/** Absolute shortest angular distance, in [0, π]. */
export function angleDist(a: number, b: number): number {
  return Math.abs(normSigned(b - a));
}

/** Is `theta` inside the arc centred on `center` with total width `spread`? */
export function inArc(theta: number, center: number, spread: number): boolean {
  return angleDist(theta, center) <= spread * 0.5;
}

/** Interpolate along the shortest arc. */
export function lerpAngle(a: number, b: number, t: number): number {
  return a + angleDelta(a, b) * t;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Smoothstep easing — used for staging flights so they arrive, not stop. */
export function ease(t: number): number {
  const c = clamp(t, 0, 1);
  return c * c * (3 - 2 * c);
}

export function easeOut(t: number): number {
  const c = clamp(t, 0, 1);
  return 1 - (1 - c) * (1 - c);
}

export interface Vec2 {
  x: number;
  y: number;
}

/** Draw-time only. */
export function toCartesian(theta: number, r: number, out: Vec2): Vec2 {
  out.x = CENTER_X + Math.cos(theta) * r;
  out.y = CENTER_Y + Math.sin(theta) * r;
  return out;
}

/** Screen point → bearing from planet centre. Used for input, not the sim loop. */
export function bearingOf(x: number, y: number): number {
  return Math.atan2(y - CENTER_Y, x - CENTER_X);
}

export function radiusOf(x: number, y: number): number {
  const dx = x - CENTER_X;
  const dy = y - CENTER_Y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Which corridor does this bearing enter through? North for the upper half,
 * south for the lower. Flank bearings (near due east/west) resolve to whichever
 * pole they lean toward and therefore come in through a corner (§6.1).
 */
export function corridorOf(bearing: number): number {
  return Math.sin(bearing) < 0 ? NORTH : SOUTH;
}
